import React, { useState, useEffect, useContext } from 'react';
import { Context as PaymentContext } from '../../contexts/PaymentContext'
import { Context as AuthContext } from '../../contexts/AuthContext'
import { useParams, useHistory as useRouterHistory } from 'react-router-dom';
import { themeColors } from '../../constants'
import {
  useHistory,
  usePayment,
  useAccountingBook,
} from '../../hooks'
import {
  FullPageLoader,
  PageHeader,
  TopRightIcon,
  PaymentForm,
  Separater
} from '../../components'

const PaymentEditPage = ({ embedded = false, paymentId = null, onSaved = null } = {}) => {
  const {
    setPayer,
    setBuilder,
    setOwers,
    setId,
    setAllocationType,
    setAccountingBookDetails,
    resetForm,
  } = useContext(PaymentContext)

  const { group_id, accounting_book_id } = useParams()
  /* eslint-disable no-unused-vars */
  const { state: authState } = useContext(AuthContext)
  const [disableForm, setDisableForm] = useState(true)
  // embedded (desktop master-detail) passes the id explicitly; the routed page
  // falls back to the :payment_id URL param inside usePayment.
  const [payment, paymentLoading] = usePayment(authState, undefined, paymentId)
  const history = useHistory();
  const routerHistory = useRouterHistory();
  const [users, accountingBookDetails, loading] = useAccountingBook(authState)
  const [_manualOwers, _setManualOwers] = useState([])
  const [_customPayers, _setCustomPayers] = useState([])
  const [_owers, _setOwers] = useState([])

  useEffect(() => {
    resetForm()
  }, [])

  useEffect(() => {
    if (users && authState.userLineIdToken) {
      let builder = users.filter(u => String(u.id) === authState.userLineIdToken)[0]
      setBuilder(builder)
    }
  }, [authState.userLineIdToken, users])

  useEffect(() => {
    if (!loading && !paymentLoading) {
      setId(payment.id)
      let payer = users.filter(u => String(u.id) === payment.payer_id)[0]
      setPayer(payer)

      // For a foreign payment the editor works in the selected currency, so seed
      // each row from the API-provided origin_amount (the settlement amount is
      // only what's stored). Falls back to amount for settlement-currency books.
      const isForeign = !!payment.origin_currency
      const rowAmount = (o) => (isForeign && o.origin_amount != null ? o.origin_amount : o.amount)

      let payers = []
      if (payment.payment_payers) {
        payers = payment.payment_payers.map(pp => {
          let user = users.filter(u => u.id === pp.user_id)[0]
          return { user: user, amount: rowAmount(pp) }
        })
      }
      _setCustomPayers(payers)

      if (payment.allocation_type === 'evenly') {
        let ower_ids = payment.allocations.map(a => a.ower_id)
        setOwers(users.filter(u => ower_ids.includes(u.id)))
        _setOwers(users.filter(u => ower_ids.includes(u.id)))
        setAllocationType('amount')

        let owers = payment.allocations.map(all => {
          let user = users.filter(u => u.id === all.ower_id)[0]
          return { user: user, amount: rowAmount(all) }
        })

        _setManualOwers(owers)
      } else if (payment.allocation_type === 'amount'){
        let owers = payment.allocations.map(all => {
          let user = users.filter(u => u.id === all.ower_id)[0]
          return { user: user, amount: rowAmount(all) }
        })

        _setManualOwers(owers)
        setAllocationType('amount')
      }

      setAccountingBookDetails(accountingBookDetails)
      setDisableForm(false)
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [paymentLoading, loading, users, accountingBookDetails])

  const handleBack = () => {
    resetForm()
    // return to wherever edit was opened (list or member page)
    if (window.history.length > 2) { routerHistory.goBack() }
    else { history.navigateTo("paymentIndexPage", { group_id, accounting_book_id }) }
  }


  const getOwers = () => {
    return users.map(user => {
      let ower = _manualOwers.filter(o => o.user.id === user.id)[0]
      let touched = false
      if (ower && ower.amount > 0) {
        touched = true
      }
      return {
        user: user,
        amount: ower ? ower.amount : '',
        touched: touched
      }
    })
  }

  const getPayers = () => {
    return users.map(user => {
      let ower = _customPayers.filter(o => o.user.id === user.id)[0]
      let touched = false
      if (ower && ower.amount > 0) {
        touched = true
      }
      return {
        user: user,
        amount: ower ? ower.amount : '',
        touched: touched
      }
    })
  }

  const form = (
    loading ?
      <FullPageLoader /> :
      (_manualOwers ?
        <PaymentForm
          payment={payment}
          authState={authState}
          users={users.filter(u => u.coverCost === true)}
          manualOwers={getOwers()}
          customPayers={getPayers()}
          owers={_manualOwers.filter(o => o.amount > 0).map(o => o.user)}
          onComplete={embedded ? onSaved : null}/> : null)
  )

  // Embedded in the desktop master-detail right pane: just the form, no page
  // chrome (the pane provides its own header). The routed page keeps its header.
  if (embedded) {
    return form
  }

  return (
    <>
      <TopRightIcon
        clicked={handleBack}
        style={{ fontSize: '20px', right: '20px', color: 'black' }} >
        <div> 取消 </div>
      </TopRightIcon>
      <PageHeader color={themeColors.gray400}>
        編輯帳款
      </PageHeader>
      <Separater style={{ margin: 0 }}/>
      {form}
    </>
  )
}

export default PaymentEditPage;
